import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import { Check, Music, Shield, Share2, X, Sparkles } from 'lucide-react';

const STORAGE_DISMISS = 'pulse_artist_first_win_dismissed';

/**
 * Onboarding-style card for artists: first upload → verification → share profile.
 * Matches OnboardingPage / ProfilePage visual language (rounded-[34px], borders, step bars).
 */
export default function ArtistFirstWinPanel({
  lang,
  songs = [],
  artistPublicId,
  className = '',
  /** 'full' = dashboard hero; 'compact' = inset in profile */
  density = 'full',
}) {
  const [dismissed, setDismissed] = useState(() =>
    typeof localStorage !== 'undefined' ? localStorage.getItem(STORAGE_DISMISS) === '1' : false
  );

  const steps = useMemo(() => {
    const hasTrack = Array.isArray(songs) && songs.length > 0;
    const st = (s) => s.status || 'approved';
    const isPending = (s) => st(s) === 'pending_review' || st(s) === 'pending';
    const anyRejected = hasTrack && songs.some((s) => st(s) === 'rejected');
    const anyPending = hasTrack && songs.some(isPending);
    const allApproved = hasTrack && songs.every((s) => st(s) === 'approved');
    return {
      uploadDone: hasTrack,
      verifyDone: allApproved,
      verifyInProgress: hasTrack && (anyPending || anyRejected),
      verifyRejected: hasTrack && anyRejected,
      shareReady: hasTrack,
    };
  }, [songs]);

  const currentStepIndex = !steps.uploadDone ? 0 : steps.verifyInProgress ? 1 : 2;

  const dismiss = () => {
    localStorage.setItem(STORAGE_DISMISS, '1');
    setDismissed(true);
  };

  if (dismissed) return null;

  const padding = density === 'compact' ? 'p-5 md:p-6' : 'p-6 md:p-10';
  const titleSize = density === 'compact' ? 'text-2xl md:text-3xl' : 'text-3xl sm:text-4xl';

  return (
    <div
      data-testid="artist-first-win-panel"
      className={`rounded-[34px] border border-white/10 bg-black/60 backdrop-blur-xl overflow-hidden ${className}`}
    >
      <div className={`${padding} border-b border-white/5`}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-xs uppercase tracking-[0.24em] text-pulse-muted mb-3 flex items-center gap-2">
              <Sparkles size={12} className="text-pulse-primary" />
              {lang === 'es' ? 'Primera victoria' : 'First win'}
            </p>
            <h2 className={`font-heading font-bold tracking-tight leading-tight ${titleSize}`}>
              {lang === 'es' ? 'Tu lanzamiento en tres pasos' : 'Your launch in three steps'}
            </h2>
            <p className="mt-3 text-sm text-white/75 max-w-xl leading-relaxed">
              {lang === 'es'
                ? 'No es decorativo: cuando completes el flujo, tu música queda lista para descubrirse y para cobrar con reglas claras.'
                : 'This is not decorative: when you finish the flow, your music is ready to be discovered and monetized with clear rules.'}
            </p>
          </div>
          <button
            type="button"
            data-testid="artist-first-win-dismiss"
            onClick={dismiss}
            className="shrink-0 p-2 rounded-full border border-white/10 text-white/60 hover:text-white hover:bg-white/5 transition-colors"
            aria-label={lang === 'es' ? 'Cerrar' : 'Dismiss'}
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex items-center gap-2 mt-8 text-xs text-pulse-muted uppercase tracking-[0.2em]">
          {[0, 1, 2].map((i) => (
            <div
              key={i}
              className={`h-1.5 flex-1 rounded-full ${currentStepIndex >= i ? 'bg-pulse-primary' : 'bg-white/10'}`}
            />
          ))}
        </div>
      </div>

      <div className={`${density === 'compact' ? 'p-5 md:p-6' : 'p-6 md:p-10'} space-y-4`}>
        <StepRow
          done={steps.uploadDone}
          active={currentStepIndex === 0}
          icon={<Music size={18} />}
          title={lang === 'es' ? 'Sube tu primera canción' : 'Upload your first track'}
          subtitle={
            lang === 'es'
              ? 'Audio + título + declaración de derechos en tu perfil. Ahí está el flujo completo.'
              : 'Audio + title + rights declaration on your profile. That is the full flow.'
          }
          action={
            !steps.uploadDone ? (
              <Link
                data-testid="artist-first-win-upload-cta"
                to="/profile#upload-song"
                className="inline-flex items-center gap-2 rounded-full bg-pulse-primary px-5 py-2.5 text-sm font-bold text-black hover:bg-pulse-primary-hover transition-colors"
              >
                {lang === 'es' ? 'Ir a subir canción' : 'Go to upload'}
              </Link>
            ) : (
              <span className="inline-flex items-center gap-1.5 text-sm text-green-400">
                <Check size={16} /> {lang === 'es' ? 'Listo' : 'Done'}
              </span>
            )
          }
        />

        <StepRow
          done={steps.verifyDone}
          active={currentStepIndex === 1}
          icon={<Shield size={18} />}
          title={lang === 'es' ? 'Verificación (Sound fingerprint)' : 'Verification (sound fingerprint)'}
          subtitle={
            lang === 'es'
              ? 'Muchas veces es automática. Si hace falta revisión manual, puede tardar hasta unas 12 h — te avisamos.'
              : 'Often automatic. If manual review is needed, it can take up to ~12h — we will notify you.'
          }
          action={
            steps.uploadDone ? (
              steps.verifyDone ? (
                <span className="inline-flex items-center gap-1.5 text-sm text-green-400">
                  <Check size={16} /> {lang === 'es' ? 'Superado' : 'Cleared'}
                </span>
              ) : steps.verifyInProgress ? (
                <span className={`text-xs ${steps.verifyRejected ? 'text-red-300/90' : 'text-amber-300/90'}`}>
                  {steps.verifyRejected
                    ? (lang === 'es' ? 'Revisar en perfil' : 'Check profile')
                    : (lang === 'es' ? 'En revisión…' : 'In review…')}
                </span>
              ) : (
                <span className="text-xs text-pulse-muted">{lang === 'es' ? 'Pendiente de señales' : 'Awaiting signals'}</span>
              )
            ) : (
              <span className="text-xs text-pulse-muted">—</span>
            )
          }
        />

        <StepRow
          done={steps.verifyDone && steps.uploadDone}
          active={currentStepIndex === 2}
          icon={<Share2 size={18} />}
          title={lang === 'es' ? 'Comparte tu perfil público' : 'Share your public profile'}
          subtitle={
            lang === 'es'
              ? 'Un solo enlace para escuchar, seguir y apoyar. Copia y pégalo donde ya está tu comunidad.'
              : 'One link to listen, follow and support. Paste it where your community already lives.'
          }
          action={
            artistPublicId ? (
              <div className="flex flex-wrap gap-2">
                <Link
                  to={`/artist/${artistPublicId}`}
                  className="inline-flex rounded-full border border-white/15 px-4 py-2 text-sm font-semibold text-white hover:bg-white/5 transition-colors"
                >
                  {lang === 'es' ? 'Ver perfil público' : 'Open public profile'}
                </Link>
                <button
                  type="button"
                  data-testid="artist-first-win-copy-link"
                  onClick={() => {
                    const url = `${window.location.origin}/artist/${artistPublicId}`;
                    navigator.clipboard?.writeText(url).then(() => {
                      toast.success(lang === 'es' ? 'Enlace copiado' : 'Link copied');
                    }).catch(() => toast.error(lang === 'es' ? 'No se pudo copiar' : 'Could not copy'));
                  }}
                  className="inline-flex rounded-full border border-pulse-primary/40 bg-pulse-primary/10 px-4 py-2 text-sm font-semibold text-pulse-primary hover:bg-pulse-primary/20 transition-colors"
                >
                  {lang === 'es' ? 'Copiar enlace' : 'Copy link'}
                </button>
              </div>
            ) : (
              <span className="text-xs text-pulse-muted">{lang === 'es' ? 'Activa tu perfil artista primero' : 'Activate your artist profile first'}</span>
            )
          }
        />
      </div>
    </div>
  );
}

function StepRow({ done, active, icon, title, subtitle, action }) {
  return (
    <div
      className={`rounded-[24px] border p-4 md:p-5 flex flex-col sm:flex-row sm:items-start gap-4 transition-colors ${
        active ? 'border-pulse-primary/35 bg-pulse-primary/[0.06]' : 'border-white/10 bg-white/[0.02]'
      }`}
    >
      <div
        className={`w-11 h-11 rounded-2xl flex items-center justify-center shrink-0 border ${
          done ? 'border-green-500/30 bg-green-500/10 text-green-300' : 'border-white/10 bg-white/5 text-pulse-primary'
        }`}
      >
        {done ? <Check size={20} className="text-green-400" /> : icon}
      </div>
      <div className="flex-1 min-w-0">
        <p className="font-heading font-semibold text-base">{title}</p>
        <p className="text-xs text-pulse-muted mt-1 leading-relaxed">{subtitle}</p>
      </div>
      <div className="shrink-0 sm:pt-0.5">{action}</div>
    </div>
  );
}
